import { Link } from "@tanstack/react-router";
import type { RouterOutputs } from "@alm/api-contracts";
import { cn } from "@/lib/utils";

/* "Mine" is not a server tab: it is the same list kept to the rows that touched this person's
 * handset. The server does not know whose browser is asking, and a tab meaning something different
 * per reader is one the counts beside it could never agree with. */
const TABS = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "attention", label: "Needs you" },
  { id: "handled", label: "Handled" },
] as const;
export type Tab = (typeof TABS)[number]["id"];
export const isTab = (value: unknown): value is Tab => TABS.some((tab) => tab.id === value);
type Counted = "all" | "attention" | "handled";
const COUNTED: ReadonlySet<string> = new Set<Counted>(["all", "attention", "handled"]);

/** The reader's own number, as the list is asked for it. Only somebody holding one sees it. */
export type Line = "mine";
export const isLine = (value: unknown): value is Line => value === "mine";

export type CallSearch = { c?: string; tab?: Tab; line?: Line; q?: string };

/** The search that keeps the list as it is: no tab means All, no line means the front desk. */
export const listSearch = (tab: Tab, line: Line | undefined, q?: string): CallSearch => ({
  ...(tab === "all" ? {} : { tab }),
  ...(line ? { line } : {}),
  ...(q ? { q } : {}),
});

/* Nothing loaded is not the same as nothing left: a building with two thousand
   calls opens on sixty, and on Mine most of those belong to somebody else. */
export function emptyTitle(tab: Tab, line: Line | undefined, more: boolean): string {
  if (line) {
    if (tab === "attention") return "Nothing on your number is waiting on you.";
    if (tab === "handled") return "No handled calls on your number yet.";
    return "No calls on your number yet.";
  }
  if (tab === "attention") return "Nobody is waiting on a call.";
  if (tab === "mine")
    return more
      ? "None of the calls loaded so far rang your extension."
      : "No calls have rung your extension yet.";
  if (tab === "handled") return "No handled calls yet.";
  return "No calls yet.";
}

type Counts = RouterOutputs["frontDesk"]["calls"]["list"]["counts"];

/** The strip above the list: which calls, with how many the database counts in each. */
export function CallTabs({
  tab,
  line,
  q,
  counts,
}: {
  tab: Tab;
  line: Line | undefined;
  q?: string;
  counts: Counts | undefined;
}) {
  /* On your own number every call is yours, so Mine would be All twice. */
  const tabs = line ? TABS.filter((entry) => entry.id !== "mine") : TABS;
  return (
    <div className="flex gap-0.5 overflow-x-auto rounded-lg bg-sarah/8 p-0.5" role="tablist">
      {tabs.map((entry) => (
        <Link
          key={entry.id}
          to="/ws/sarah/calls"
          search={listSearch(entry.id, line, q)}
          role="tab"
          aria-selected={tab === entry.id}
          className={cn(
            "flex-1 rounded-md px-2 py-1.5 text-center text-xs font-medium whitespace-nowrap transition-colors pointer-coarse:py-2",
            tab === entry.id
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {entry.label}
          {counts && COUNTED.has(entry.id) ? (
            <span className="text-muted-foreground ml-1 tabular-nums">
              {counts[entry.id as Counted]}
            </span>
          ) : null}
        </Link>
      ))}
    </div>
  );
}
